import { TractModule } from '@/modules/TractModule';
import { LayerComponentBase } from '@/stellar-globe-components';
import { clip, V4, ad2xyz, xyz2ad } from '@/utils/math';
import { deg2rad } from '@hscmap/angle';
import { Layer, path, View } from "@hscmap/stellar-globe";
import { mat4 } from 'gl-matrix';
import { Component, Prop, Vue } from "vue-property-decorator";
import { CreateElement } from 'vue';

class Renderer extends path.Renderer {
  constructor(gl: WebGLRenderingContext) {
    super(gl)
  }

  refreshArray(size: number, showPatch: boolean) {
    this.darkenNarrowLine = false
    this.minWidth = 3
    const s = size
    const w = 0.
    const tractColor: V4 = [0, 1, 0, 0.5]
    const patchColor: V4 = [0, 1, 1, 0.25]
    const paths: path.Path[] = []
    paths.push(new path.Path([ // tract grid
      new path.Point([1, -s, -s], tractColor, w),
      new path.Point([1, -s, +s], tractColor, w),
      new path.Point([1, +s, +s], tractColor, w),
      new path.Point([1, +s, -s], tractColor, w),
    ], true, path.JOINT.MITER))
    if (showPatch) {
      for (let i = 1; i < 9; ++i) {
        const t = 2 * s * (-0.5 + (i / 9))
        paths.push(new path.Path([
          new path.Point([1, t, -s], patchColor, w),
          new path.Point([1, t, +s], patchColor, w),
        ], false))
        paths.push(new path.Path([
          new path.Point([1, -s, t], patchColor, w),
          new path.Point([1, +s, t], patchColor, w),
        ], false))
      }
    }
    this.setPaths(paths)
  }

  a = 0
  d = 0

  protected mMatirx() {
    const cA = Math.cos(this.a)
    const sA = Math.sin(this.a)
    const cD = Math.cos(this.d)
    const sD = Math.sin(this.d)
    return mat4.fromValues(
      cA * cD, cD * sA, -sD, 0,
      -sA, cA, 0, 0,
      cA * sD, sA * sD, cD, 0,
      0, 0, 0, 1)
  }
}

class TractFrameLayer extends Layer {
  private pr!: Renderer

  constructor(readonly vm: TractFrame) {
    super()
  }

  private unwatch!: () => void

  onAdd() {
    const pixelScale = 0.168 // arcsec / pixel
    const tractSize = 36000 // in pixels
    const size = 0.5 * tractSize * deg2rad(pixelScale / 3600)
    this.pr = new Renderer(this.globe.gl)
    this.unwatch = this.vm.$watch(() => this.vm.module.showPatch, (showWatch) => {
      this.pr.refreshArray(size, showWatch)
      this.globe.requestRefresh()
    }, { immediate: true })
  }

  onRemove() {
    this.unwatch()
    this.pr.release()
  }

  render(view: View) {
    this.pr.alpha = clip(1 - view.camera.arc, 0, 1)
    const [a, d] = xyz2ad(...view.camera.direction)
    const { rt, nw, nh } = this.vm.module
    rt.tractCenters(a, d, nw, nh, (aa, dd) => {
      this.pr.a = aa
      this.pr.d = -dd
      this.pr.render(view)
    })
  }
}

@Component
class TractFrame extends LayerComponentBase<TractFrameLayer> {
  @Prop({ required: true })
  module!: TractModule

  newLayer() {
    return new TractFrameLayer(this)
  }
}

@Component
export default class extends Vue {
  @Prop({ required: true })
  module!: TractModule

  render(h: CreateElement) {
    return this.module.show ? h(TractFrame, { props: { module: this.module } }) : undefined
  }
}
